import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, Box, Typography, Button, TextField, Alert, Link, Chip } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { Elements } from '@stripe/react-stripe-js';
import { BACKEND_URL } from '../utils/constants';
import { getPendingReferral, clearPendingReferral } from '../utils/referral';
import EmbeddedCheckoutForm, { stripePromise } from './EmbeddedCheckoutForm';
import { extractApiError } from '../utils/apiError';

import { PixelDots } from './ConstructionLoader';
export type PlanTier = 'freemium' | 'pro' | 'max';

type AppliedReferral = {
  code: string;
  percentOff: number | null;
  duration: string | null;
};

type Props = {
  /** Code already attributed to this user, if any (prefills / shows as applied). */
  initialReferralCode?: string | null;
  onChoosePlan: (tier: PlanTier, referralCode: string | null) => void;
  onSkip: () => void;
  onBack?: () => void;
};

const PLANS: { tier: PlanTier; name: string; price: number }[] = [
  { tier: 'freemium', name: 'Freemium', price: 0 },
  { tier: 'pro', name: 'Pro', price: 20 },
  { tier: 'max', name: 'Max', price: 50 },
];

// Same wording as the Settings comparison table, so the two pages can't drift.
const FEATURES: { feature: string; freemium: string; pro: string; max: string }[] = [
  { feature: 'Multifamily Model', freemium: '✨ Unlimited access', pro: '✨ Unlimited access', max: '✨ Unlimited access' },
  { feature: 'Mixed-Use Model', freemium: '🔒 Unlock with Pro', pro: '🚀 Included', max: '🚀 Included' },
  { feature: 'Industrial & Retail Model', freemium: '🔒 Unlock with Pro', pro: '🚀 Included', max: '🚀 Included' },
  { feature: 'MF & Mixed-Use Development Suite', freemium: '🔒 Exclusive to Max', pro: '🔒 Upgrade to Max', max: '🌟 Premium Access' },
  { feature: 'Excel Export', freemium: '🔒 Pro feature', pro: '📊 Export anytime', max: '📊 Export anytime' },
  { feature: 'PDF Reports', freemium: '🔒 Pro feature', pro: '🧾 Download ready', max: '🧾 Download ready' },
];

export default function OnboardingStepThree({ initialReferralCode, onChoosePlan, onSkip, onBack }: Props) {
  const { getAccessTokenSilently } = useAuth0();
  const [selected, setSelected] = useState<PlanTier | null>(null);
  const [applied, setApplied] = useState<AppliedReferral | null>(null);
  const [showCodeField, setShowCodeField] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [autoChecking, setAutoChecking] = useState(true);
  // Payment is collected inline once a paid plan is chosen, via the same
  // SetupIntent path Settings uses — not a second checkout implementation.
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [payingFor, setPayingFor] = useState<PlanTier | null>(null);
  const [paymentError, setPaymentError] = useState('');
  const [preparingPayment, setPreparingPayment] = useState(false);

  const claim = useCallback(async (code: string): Promise<boolean> => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
    });
    const resp = await fetch(`${BACKEND_URL}/api/billing/claim-referral`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ promo_code: code })
    });
    const data = await resp.json().catch(() => ({} as any));
    if (!resp.ok || !data.valid) {
      setCodeError(data.error || "That referral code isn't valid. Check the spelling, or leave it blank.");
      return false;
    }
    // `referral_code` is whichever code actually owns this user — with
    // write-once semantics that may be an earlier one, not what we just sent.
    setApplied({
      code: data.referral_code || data.code,
      percentOff: data.percent_off ?? null,
      duration: data.duration ?? null,
    });
    setCodeError('');
    return true;
  }, [getAccessTokenSilently]);

  // A code can arrive two ways: already attributed to the user, or sitting in
  // sessionStorage from a ?ref= link. Either way it shows as applied, never as
  // an empty box the user has to think about.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (initialReferralCode) {
          if (!cancelled) await claim(initialReferralCode);
          return;
        }
        const pending = getPendingReferral();
        if (pending) {
          const ok = await claim(pending);
          if (ok) clearPendingReferral();
        }
      } catch {
        /* a failed pre-apply must not block plan selection */
      } finally {
        if (!cancelled) setAutoChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialReferralCode, claim]);

  const handleApplyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    setClaiming(true);
    try {
      await claim(code);
    } catch {
      setCodeError("Couldn't check that code right now. Please try again.");
    } finally {
      setClaiming(false);
    }
  };

  const handleContinue = async () => {
    if (!selected) return;
    // Freemium needs no payment: they already have that subscription, and the
    // referral code (if any) is already attributed.
    if (selected === 'freemium') {
      onChoosePlan('freemium', applied?.code ?? null);
      return;
    }
    setPaymentError('');
    setPreparingPayment(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
      });
      const r = await fetch(`${BACKEND_URL}/api/billing/setup-intent`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      if (!r.ok) {
        setPaymentError(await extractApiError(r, 'Could not initialize payment.'));
        return;
      }
      const d = await r.json();
      setClientSecret(d.clientSecret);
      setPayingFor(selected);
    } catch {
      setPaymentError('Could not initialize payment. Please try again.');
    } finally {
      setPreparingPayment(false);
    }
  };

  const discountLine = (price: number): string | null => {
    if (!applied || !applied.percentOff || price === 0) return null;
    const discounted = price * (1 - applied.percentOff / 100);
    const per = applied.duration === 'once' ? 'first month' : 'while active';
    return `${applied.code}: $${discounted.toFixed(0)} ${per}`;
  };

  return (
    <Card sx={{ width: '100%', maxWidth: 860, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', borderRadius: 2 }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>Step 3 of 3</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ height: 8, width: 64, backgroundColor: '#3b82f6', borderRadius: 1 }} />
              <Box sx={{ height: 8, width: 64, backgroundColor: '#3b82f6', borderRadius: 1 }} />
              <Box sx={{ height: 8, width: 64, backgroundColor: '#3b82f6', borderRadius: 1 }} />
            </Box>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Pick a plan</Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Start free, or unlock every model type. You can change plans any time in Settings.
          </Typography>
        </Box>

        {/* Referral: shown as applied when we have one, otherwise tucked behind a link */}
        {autoChecking ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <PixelDots surface="light" label="Checking your referral code" />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Checking your referral code…</Typography>
          </Box>
        ) : applied ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            <strong>{applied.code} applied</strong>
            {applied.percentOff ? ` · ${applied.percentOff}% off your ${applied.duration === 'once' ? 'first month' : 'subscription'}` : ''}
          </Alert>
        ) : (
          <Box sx={{ mb: 3 }}>
            {!showCodeField ? (
              <Link component="button" type="button" underline="hover" onClick={() => setShowCodeField(true)}
                sx={{ fontSize: 14 }}>
                Have a referral code?
              </Link>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', maxWidth: 420 }}>
                <TextField
                  label="Referral code"
                  value={codeInput}
                  onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setCodeError(''); }}
                  size="small"
                  fullWidth
                  autoFocus
                  error={!!codeError}
                  helperText={codeError || ' '}
                  inputProps={{ maxLength: 32, style: { textTransform: 'uppercase' } }}
                />
                <Button onClick={handleApplyCode} disabled={claiming || !codeInput.trim()} sx={{ mt: 0.5 }}>
                  {claiming ? 'Checking…' : 'Apply'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {paymentError && <Alert severity="error" sx={{ mb: 2 }}>{paymentError}</Alert>}

        {payingFor && clientSecret ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {payingFor === 'pro' ? 'Pro' : 'Max'} — ${PLANS.find((p) => p.tier === payingFor)?.price}/mo
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {applied?.percentOff
                ? `${applied.code} takes ${applied.percentOff}% off your first month. Cancel anytime.`
                : 'Billed monthly. Cancel anytime.'}
            </Typography>
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckoutForm
                tier={payingFor}
                initialPromoCode={applied?.code ?? null}
                submitLabel="Confirm and start"
                onSuccess={() => onChoosePlan(payingFor, applied?.code ?? null)}
              />
            </Elements>
            <Button size="small" sx={{ mt: 2 }} onClick={() => { setPayingFor(null); setClientSecret(null); }}>
              Back to plans
            </Button>
          </Box>
        ) : (
        <>
        {/* Plan cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          {PLANS.map((plan) => {
            const isSelected = selected === plan.tier;
            const disc = discountLine(plan.price);
            return (
              <Box
                key={plan.tier}
                onClick={() => setSelected(plan.tier)}
                sx={{
                  p: 2.5, borderRadius: 2, cursor: 'pointer',
                  border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  backgroundColor: isSelected ? 'rgba(59,130,246,0.04)' : 'transparent',
                  transition: 'border-color .15s, background-color .15s',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{plan.name}</Typography>
                {plan.price === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>Free forever</Typography>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      ${plan.price}/mo · cancel anytime
                    </Typography>
                    {disc && <Chip size="small" color="success" variant="outlined" label={disc} sx={{ mt: 1 }} />}
                  </>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Feature comparison */}
        <Box sx={{ overflowX: 'auto', mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', minWidth: 640, rowGap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Compare plans</Typography>
            {PLANS.map((p) => (
              <Typography key={p.tier} variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{p.name}</Typography>
            ))}
            {FEATURES.map((row) => (
              <React.Fragment key={row.feature}>
                <Typography variant="body2" sx={{ py: 0.75, fontWeight: 600 }}>{row.feature}</Typography>
                <Typography variant="body2" sx={{ py: 0.75 }}>{row.freemium}</Typography>
                <Typography variant="body2" sx={{ py: 0.75 }}>{row.pro}</Typography>
                <Typography variant="body2" sx={{ py: 0.75 }}>{row.max}</Typography>
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <Button
          fullWidth variant="contained" size="large" disabled={!selected || preparingPayment}
          onClick={handleContinue}
          sx={{ py: 1.5, mb: 1 }}
        >
          {preparingPayment
            ? 'Preparing…'
            : selected === 'freemium'
              ? 'Start on Freemium'
              : selected
                ? `Continue with ${selected === 'pro' ? 'Pro' : 'Max'}`
                : 'Select a plan'}
        </Button>
        </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
          {onBack && <Button onClick={onBack} size="small">Back</Button>}
          {/* Requirement: never dead-end anyone behind a paywall. */}
          <Button onClick={onSkip} size="small">Skip for now</Button>
        </Box>
      </CardContent>
    </Card>
  );
}
